import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export const useChatMessages = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  // Фетч історії з автоматичним кешуванням
  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['chat-messages', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      const { data, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      console.table({
        fetchedCount: data?.length || 0,
        firstMessage: data?.[0]?.created_at,
        lastMessage: data?.at(-1)?.created_at,
      });

      return data.map(msg => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        timestamp: new Date(msg.created_at),
      }));
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 хвилин
    gcTime: 30 * 60 * 1000, // 30 хвилин в кеші
  });

  // Mutation для збереження повідомлення
  const addMessage = useMutation({
    mutationFn: async ({ role, content }: { role: 'user' | 'assistant'; content: string }) => {
      if (!userId) throw new Error('No user');
      
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({ user_id: userId, role, content })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onMutate: async ({ role, content }) => {
      // Оптимістичне оновлення
      await queryClient.cancelQueries({ queryKey: ['chat-messages', userId] });
      const previous = queryClient.getQueryData(['chat-messages', userId]);

      queryClient.setQueryData(['chat-messages', userId], (old: Message[] = []) => [
        ...old,
        {
          id: `temp-${Date.now()}`,
          role,
          content,
          timestamp: new Date(),
        },
      ]);

      return { previous };
    },
    onError: (err, variables, context) => {
      // Відкат при помилці
      if (context?.previous) {
        queryClient.setQueryData(['chat-messages', userId], context.previous);
      }
      console.error('Error saving message:', err);
    },
    onSuccess: () => {
      // Інвалідація після успіху
      queryClient.invalidateQueries({ queryKey: ['chat-messages', userId] });
    },
  });

  // Realtime підписка для синхронізації між табами/сесіями
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`chat:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          queryClient.setQueryData(['chat-messages', userId], (old: Message[] = []) => {
            // Перевірка на дублікати
            if (old.some(msg => msg.id === payload.new.id)) return old;
            
            return [...old, {
              id: payload.new.id,
              role: payload.new.role,
              content: payload.new.content,
              timestamp: new Date(payload.new.created_at),
            }];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  return {
    messages,
    isLoading,
    addMessage: addMessage.mutate,
  };
};
